import { useCallback, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import effects from 'app/effects/effects'
import { messageAlertError } from 'commons/error'
import {
  COMMENTS_ENTITY_TYPE,
  COMMENTS_CREATOR_TYPE,
} from 'app/constants/messageDetail'

import MessageAlert from 'app/components/MessageAlert'

export const ActionType = {
  // update Comment
  SET_IS_UPDATING_COMMENT: 'SET_IS_UPDATING_COMMENT',
  SET_IS_UPDATE_COMMENT_FAIL: 'SET_IS_UPDATE_COMMENT_FAIL',
  SET_IS_UPDATE_COMMENT_SUCCESS: 'SET_IS_UPDATE_COMMENT_SUCCESS',
  // create Reply
  SET_IS_SUBMITING_REPLY: 'SET_IS_SUBMITING_REPLY',
  SET_IS_SUBMIT_REPLY_FAIL: 'SET_IS_SUBMIT_REPLY_FAIL',
  SET_IS_SUBMIT_REPLY_SUCCESS: 'SET_IS_SUBMIT_REPLY_SUCCESS',
  // delete Comment
  SET_IS_DELETE_COMMENT_SUCCESS: 'SET_IS_DELETE_COMMENT_SUCCESS',
  RESET: 'RESET',
}

const initialState = {
  // update Comment
  isUpdatingComment: false,
  isUpdateCommentSuccess: false,
  // create Reply
  isSubmitingReply: false,
  isSubmitReplySuccess: false,
  // delete Comment
  isDeleteCommentSuccess: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_IS_UPDATING_COMMENT:
      return {
        ...state,
        isUpdatingComment: action.value,
      }
    case ActionType.SET_IS_UPDATE_COMMENT_SUCCESS:
      return {
        ...state,
        isUpdatingComment: false,
        isUpdateCommentSuccess: true,
      }
    case ActionType.SET_IS_UPDATE_COMMENT_FAIL:
      return {
        ...state,
        isUpdatingComment: false,
        isUpdateCommentSuccess: false,
      }
    case ActionType.SET_IS_SUBMITING_REPLY:
      return {
        ...state,
        isSubmitingReply: action.value,
      }
    case ActionType.SET_IS_SUBMIT_REPLY_SUCCESS:
      return {
        ...state,
        isSubmitingReply: false,
        isSubmitReplySuccess: true,
      }
    case ActionType.SET_IS_SUBMIT_REPLY_FAIL:
      return {
        ...state,
        isSubmitingReply: false,
        isSubmitReplySuccess: false,
      }
    case ActionType.SET_IS_DELETE_COMMENT_SUCCESS:
      return {
        ...state,
        isDeleteCommentSuccess: action.value,
      }
    case ActionType.RESET:
      return initialState
    default:
      return state
  }
}

export default function useCommentBox(
  notiId,
  commentId,
  createId,
  onFetchCommentList = () => {},
) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()

  const handleAlertError = useCallback(
    (error) => {
      return commonDispatch(
        MessageAlert.showError({
          message: messageAlertError(error, formatMessage),
        }),
      )
    },
    [commonDispatch, formatMessage],
  )

  const onUpdateComment = async (value) => {
    dispatch({
      type: 'SET_IS_UPDATING_COMMENT',
      value: true,
    })
    effects.apis
      .updateComment(notiId, value, commentId)
      .then(async () => {
        dispatch({
          type: 'SET_IS_UPDATE_COMMENT_SUCCESS',
        })
        onFetchCommentList()
      })
      .catch((error) => {
        handleAlertError(error)
        dispatch({
          type: 'SET_IS_UPDATE_COMMENT_FAIL',
        })
      })
  }

  const onSubmittedReply = async (value) => {
    dispatch({
      type: 'SET_IS_SUBMITING_REPLY',
      value: true,
    })
    await effects.apis
      .addComment(notiId, {
        ...value,
        entityType: COMMENTS_ENTITY_TYPE,
        entityId: notiId,
        parentId: commentId,
        creatorType: COMMENTS_CREATOR_TYPE,
        creatorId: createId,
      })
      .then(async () => {
        dispatch({
          type: 'SET_IS_SUBMIT_REPLY_SUCCESS',
        })
        onFetchCommentList()
      })
      .catch((error) => {
        dispatch({
          type: 'SET_IS_SUBMIT_REPLY_FAIL',
        })
        handleAlertError(error)
      })
  }

  const onDeleteComment = async () => {
    await effects.apis
      .deleteComment(notiId, commentId)
      .then(async () => {
        dispatch({
          type: 'SET_IS_DELETE_COMMENT_SUCCESS',
          value: true,
        })
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'draft.notification.delete.success.message',
            }),
          }),
        )
        onFetchCommentList()
      })
      .catch((error) => {
        dispatch({
          type: 'SET_IS_DELETE_COMMENT_SUCCESS',
          value: false,
        })
        handleAlertError(error)
      })
  }

  const reset = () => {
    dispatch({
      type: 'RESET',
    })
  }

  return {
    state,
    reset,
    onUpdateComment,
    onDeleteComment,
    onSubmittedReply,
  }
}
